import * as React from 'react';
import { GeoSuggestionField, TagsList } from '../../../../components/GeoSuggestionField';
import { useGeoTagsSuggest } from '../../utils/useGeoTagsSuggest';
import { useGeoTagsSuggestValue } from '../../utils/useGeoTagsSuggestValue';
import { useGeoTagsItemClick } from '../../utils/useGeoTagsItemClick';
import { IGeoStep, IGeoValue } from '../../types';
import { useGeoTagsInputBlur } from '../../utils/useGeoTagsInputBlur';
import { ILocation } from '../../../../types/location';
import { useTags } from '../../utils/useTags';
import { SubmitButton } from '../../../../components/SubmitButton';
import { useFormTracking } from '../../../../utils/useFormTracking';

export interface IGeoTagsContainerProps {
  location: ILocation;
  stepConfig: IGeoStep;
  geoValues: IGeoValue[];
  inputName: string;

  onSubmit(): void;

  onGeoValuesChange(values: IGeoValue[]): void;
}

export const GeoTagsContainer: React.FC<React.PropsWithChildren<IGeoTagsContainerProps>> = ({
  location,
  stepConfig,
  geoValues,
  onGeoValuesChange,
  onSubmit,
  inputName,
}) => {
  const { isFetching, suggestError, suggestData, getSuggestions, clearSuggestionData } = useGeoTagsSuggest({
    types: stepConfig.types,
    location,
  });

  const { trackSubmit, trackChange } = useFormTracking();

  const { onValueChange, suggestValue, clearSuggestValue } = useGeoTagsSuggestValue({ getSuggestions });
  const onItemClick = useGeoTagsItemClick({ clearSuggestValue, onGeoValuesChange, geoValues });
  const onBlur = useGeoTagsInputBlur({ clearSuggestionData, clearSuggestValue });
  const { tags, onCloseTag } = useTags({ geoValues, onGeoValuesChange });

  const handleSuggestValueChange = React.useCallback(
    (value: string) => {
      trackChange();

      onValueChange(value);
    },
    [trackChange, onValueChange],
  );

  const handleSubmit = React.useCallback(() => {
    onSubmit();

    trackSubmit({
      field: stepConfig.field,
      value: tags.map(({ value }) => value).join(', '),
      isRequired: !!stepConfig.isRequired,
    });
  }, [trackSubmit, onSubmit, tags, stepConfig]);

  return (
    <div data-testid="suggest-field-container">
      <GeoSuggestionField
        value={suggestValue}
        placeholder={
          geoValues && geoValues.length && stepConfig.placeholderExtra
            ? stepConfig.placeholderExtra
            : stepConfig.placeholder
        }
        suggestData={suggestData}
        suggestError={suggestError}
        onItemClick={onItemClick}
        onValueChange={handleSuggestValueChange}
        onBlur={onBlur}
        isLoading={isFetching}
        inputName={inputName}
      />

      {!!tags.length && <TagsList options={tags} onClose={onCloseTag} />}

      <SubmitButton disabled={!location} onClick={handleSubmit}>
        Дальше
      </SubmitButton>
    </div>
  );
};

GeoTagsContainer.displayName = 'GeoTagsContainer';
